import styled from 'styled-components';
import { CheckmarkIc } from '@dsch/dd-icons';

import { media } from 'helpers/breakpoints';
import { InfoTitle } from 'components/Toolkit/InfoTitle/InfoTitle';
import { DetailList } from 'components/Toolkit/DetailList/DetailList';
import { Link } from 'components/Toolkit/Button/Link';
import { OverflowFade } from 'components/Toolkit/OverflowFade/OverflowFade';

export interface SpecialInfoPanelProps {
  id?: string;
  hero?: SpecialInfoPanelHero;
  benefits?: string[];
  moreInfo?: Link;
  description?: string;
  terms?: {
    text: string;
    link: Link;
  };
  className?: string;
}

type SpecialInfoPanelHero = {
  photo?: {
    small: string;
    large: string;
  };
  logo?: string;
  title?: string;
  subtitle?: string;
};

type Link = {
  title?: string;
  url?: string;
  external?: boolean;
};

const Container = styled.div`
  background: ${({ theme }) => theme.colors.WHITE};
  border-radius: ${({ theme }) => theme.borderRadius.default};
`;

const Header = styled.div`
  padding: ${({ theme }) => theme.spacing.S12};
`;

const TextContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.M16};

  ${media.large} {
    padding: ${({ theme }) => theme.spacing.M24};
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 80px;

  ${media.medium} {
    height: 190px;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const SIoCheckmark = styled(CheckmarkIc)`
  color: ${({ theme }) => theme.colors.GREEN};
`;

const Description = styled.p`
  ${({ theme }) => theme.fontSize.B14};
  margin-bottom: ${({ theme }) => theme.spacing.M16};

  ${media.large} {
    margin-bottom: ${({ theme }) => theme.spacing.M24};
  }
`;

const BenefitsTitle = styled.p`
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  ${({ theme }) => theme.fontSize.B14};
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

const SDetailList = styled(DetailList)`
  margin-bottom: ${({ theme }) => theme.spacing.M24};
`;

const TermsAndConditions = styled.p`
  ${({ theme }) => theme.fontSize.B14};
  margin-bottom: ${({ theme }) => theme.spacing.M24};

  ${media.large} {
    margin-bottom: ${({ theme }) => theme.spacing.M32};
  }
`;

const ManufacturerLink = styled(Link)`
  margin-bottom: ${({ theme }) => theme.spacing.M16};
`;

function SpecialInfoPanel({
  className,
  hero,
  benefits,
  moreInfo,
  description,
  terms,
}: SpecialInfoPanelProps) {
  return (
    <Container className={className}>
      <Header>
        {hero?.title && hero?.subtitle && hero?.logo && (
          <InfoTitle
            title={hero.title}
            listItems={[hero.subtitle]}
            illustration={hero.logo}
          />
        )}
      </Header>
      <ImageContainer>
        <HeroImage src={`${hero?.photo?.large}`} alt="" />
      </ImageContainer>
      <TextContainer>
        {description && <Description>{description}</Description>}
        <BenefitsTitle>What you can expect:</BenefitsTitle>
        <OverflowFade
          mobileContainerMaxHeight={204}
          desktopContainerMaxHeight={172}
        >
          {benefits && (
            <SDetailList
              list={benefits?.map((benefit) => ({
                title: benefit,
                icon: <SIoCheckmark width="16" height="16" />,
              }))}
            />
          )}
          <ManufacturerLink
            ofType="SECONDARY"
            href={moreInfo?.url}
            target={moreInfo?.external ? '_blank' : '_self'}
            rel={moreInfo?.external ? 'noopener' : undefined}
          >
            {moreInfo?.title}
          </ManufacturerLink>
          <TermsAndConditions>
            {terms?.text}{' '}
            <Link
              ofType="SECONDARY"
              href={terms?.link.url}
              target={terms?.link?.external ? '_blank' : '_self'}
              rel={terms?.link?.external ? 'noopener' : undefined}
              fontSize="B14"
              inline
            >
              {terms?.link.url}
            </Link>
          </TermsAndConditions>
        </OverflowFade>
      </TextContainer>
    </Container>
  );
}

export { SpecialInfoPanel };
